"use client";

import { Check, ChevronDown } from "lucide-react";
import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent,
} from "react";
import { cn } from "@/lib/utils";

export interface CustomSelectOption {
  value: string;
  label: string;
  disabled?: boolean;
  description?: string;
}

export type SelectOptionInput =
  | CustomSelectOption
  | [string, string]
  | string;

export interface CustomSelectProps {
  label: string;
  value: string;
  options: SelectOptionInput[];
  onChange: (value: string) => void;
  disabled?: boolean;
  variant?: "dark" | "light";
  size?: "sm" | "md";
  className?: string;
  menuClassName?: string;
  align?: "left" | "right";
  title?: string;
  id?: string;
}

function normalizeOption(item: SelectOptionInput): CustomSelectOption {
  if (typeof item === "string") {
    return { value: item, label: item };
  }
  if (Array.isArray(item)) {
    return { value: item[0], label: item[1] };
  }
  return item;
}

export function CustomSelect({
  label,
  value,
  options: rawOptions,
  onChange,
  disabled = false,
  variant = "dark",
  size = "md",
  className,
  menuClassName,
  align = "left",
  title,
  id: propId,
}: CustomSelectProps) {
  const generatedId = useId();
  const selectId = propId ?? generatedId;
  const listboxId = `${selectId}-listbox`;

  const [isOpen, setIsOpen] = useState(false);
  const [highlightedIndex, setHighlightedIndex] = useState(-1);

  const containerRef = useRef<HTMLDivElement>(null);
  const buttonRef = useRef<HTMLButtonElement>(null);
  const listboxRef = useRef<HTMLUListElement>(null);

  const options = useMemo(
    () => rawOptions.map(normalizeOption),
    [rawOptions],
  );

  const selectedOption = useMemo(
    () => options.find((opt) => opt.value === value) ?? options[0],
    [options, value],
  );

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setHighlightedIndex(-1);
  }, []);

  const openMenu = useCallback(() => {
    if (disabled) return;
    setIsOpen(true);
    const currentIndex = options.findIndex((opt) => opt.value === value);
    setHighlightedIndex(currentIndex >= 0 ? currentIndex : 0);
  }, [disabled, options, value]);

  const toggleMenu = useCallback(() => {
    if (isOpen) {
      closeMenu();
    } else {
      openMenu();
    }
  }, [isOpen, closeMenu, openMenu]);

  const selectOption = useCallback(
    (option: CustomSelectOption) => {
      if (option.disabled) return;
      onChange(option.value);
      closeMenu();
      buttonRef.current?.focus();
    },
    [onChange, closeMenu],
  );

  // Close on outside click
  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (e: MouseEvent | TouchEvent) => {
      if (
        containerRef.current &&
        !containerRef.current.contains(e.target as Node)
      ) {
        closeMenu();
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);
    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isOpen, closeMenu]);

  // Keyboard navigation
  const handleKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    if (disabled) return;

    switch (e.key) {
      case "Enter":
      case " ":
        e.preventDefault();
        if (isOpen) {
          if (highlightedIndex >= 0 && highlightedIndex < options.length) {
            selectOption(options[highlightedIndex]);
          } else {
            closeMenu();
          }
        } else {
          openMenu();
        }
        break;

      case "ArrowDown":
        e.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          setHighlightedIndex((prev) => {
            let next = prev + 1;
            while (next < options.length && options[next].disabled) {
              next++;
            }
            return next < options.length ? next : prev;
          });
        }
        break;

      case "ArrowUp":
        e.preventDefault();
        if (!isOpen) {
          openMenu();
        } else {
          setHighlightedIndex((prev) => {
            let next = prev - 1;
            while (next >= 0 && options[next].disabled) {
              next--;
            }
            return next >= 0 ? next : prev;
          });
        }
        break;

      case "Escape":
      case "Tab":
        if (isOpen) {
          closeMenu();
        }
        break;

      case "Home":
        if (isOpen) {
          e.preventDefault();
          const firstNonDisabled = options.findIndex((opt) => !opt.disabled);
          if (firstNonDisabled >= 0) setHighlightedIndex(firstNonDisabled);
        }
        break;

      case "End":
        if (isOpen) {
          e.preventDefault();
          for (let i = options.length - 1; i >= 0; i--) {
            if (!options[i].disabled) {
              setHighlightedIndex(i);
              break;
            }
          }
        }
        break;
    }
  };

  const isDark = variant === "dark";

  return (
    <div
      ref={containerRef}
      className="relative inline-flex shrink-0 select-none text-left"
    >
      <button
        ref={buttonRef}
        type="button"
        id={selectId}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={isOpen ? listboxId : undefined}
        aria-label={label}
        title={title ?? label}
        disabled={disabled}
        onClick={toggleMenu}
        onKeyDown={handleKeyDown}
        className={cn(
          "group relative flex shrink-0 items-center justify-between gap-2 rounded-lg font-semibold outline-none transition",
          size === "sm"
            ? "h-7 pl-2.5 pr-7 text-[11.5px]"
            : "h-8 pl-3 pr-8 text-[12.5px]",
          isDark
            ? "border border-white/10 bg-white/[.05] text-white hover:border-white/20 hover:bg-white/[.08] focus-visible:border-lime/60 focus-visible:ring-2 focus-visible:ring-lime/20"
            : "border border-line bg-white text-ink hover:border-black/20 hover:bg-black/[.02] focus-visible:border-green focus-visible:ring-2 focus-visible:ring-green/20",
          isOpen &&
            (isDark
              ? "border-lime/60 bg-white/[.08] ring-2 ring-lime/20"
              : "border-green bg-black/[.02] ring-2 ring-green/20"),
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <span className="truncate">
          {selectedOption?.label ?? label}
        </span>
        <ChevronDown
          size={13}
          aria-hidden="true"
          className={cn(
            "pointer-events-none absolute right-2.5 top-1/2 -translate-y-1/2 transition-transform duration-200",
            isDark ? "text-white/50 group-hover:text-white/80" : "text-muted group-hover:text-ink",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <ul
          ref={listboxRef}
          id={listboxId}
          role="listbox"
          aria-label={label}
          tabIndex={-1}
          className={cn(
            "no-scrollbar absolute top-full mt-1.5 z-[100] max-h-72 min-w-[190px] overflow-y-auto rounded-xl p-1 shadow-2xl backdrop-blur-xl animate-in fade-in-0 zoom-in-95 duration-100",
            align === "right" ? "right-0" : "left-0",
            isDark
              ? "border border-white/15 bg-[#121815]/95 shadow-[0_16px_36px_rgba(0,0,0,0.65)] ring-1 ring-white/10"
              : "border border-line bg-white/98 shadow-[0_16px_36px_rgba(21,35,27,0.18)] ring-1 ring-black/5",
            menuClassName,
          )}
        >
          {options.map((option, index) => {
            const isSelected = option.value === value;
            const isHighlighted = index === highlightedIndex;

            return (
              <li
                key={option.value}
                role="option"
                aria-selected={isSelected}
                aria-disabled={option.disabled}
                onClick={() => selectOption(option)}
                onMouseEnter={() => {
                  if (!option.disabled) setHighlightedIndex(index);
                }}
                className={cn(
                  "relative flex cursor-pointer items-center justify-between gap-3 rounded-lg px-2.5 py-1.5 text-[12.5px] transition-colors select-none",
                  option.disabled
                    ? isDark
                      ? "cursor-not-allowed text-white/35"
                      : "cursor-not-allowed text-black/35"
                    : isSelected
                      ? isDark
                        ? "bg-lime/15 font-semibold text-lime"
                        : "bg-[#0a7653]/10 font-semibold text-[#0a7653]"
                      : isHighlighted
                        ? isDark
                          ? "bg-white/[.08] text-white"
                          : "bg-wash text-ink"
                        : isDark
                          ? "text-white/80 hover:text-white"
                          : "text-ink/80 hover:text-ink",
                )}
              >
                <div className="flex min-w-0 flex-col">
                  <span className="truncate">{option.label}</span>
                  {option.description && (
                    <span
                      className={cn(
                        "text-[10.5px] font-normal leading-tight",
                        isDark ? "text-white/45" : "text-muted",
                      )}
                    >
                      {option.description}
                    </span>
                  )}
                </div>

                {isSelected && (
                  <Check
                    size={14}
                    aria-hidden="true"
                    className={cn(
                      "shrink-0",
                      isDark ? "text-lime" : "text-[#0a7653]",
                    )}
                  />
                )}
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
